"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Send } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { ContactFormContent } from "@/features/contact/types/contact-page-content";
interface ContactFormProps {
  content: ContactFormContent;
}
type FormState = {
  name: string;
  email: string;
  subject: string;
  message: string;
};
const INITIAL: FormState = {
  name: "",
  email: "",
  subject: "",
  message: ""
};
export function ContactForm(t0) {
  const $ = _c(12);
  const {
    content
  } = t0;
  const [form, setForm] = useState(INITIAL);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function handleChange(e) {
      setForm(prev => ({
        ...prev,
        [e.target.name]: e.target.value
      }));
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleChange = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = async function handleSubmit(e_0) {
      e_0.preventDefault();
      setLoading(true);
      await new Promise(_temp);
      setLoading(false);
      setSubmitted(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleSubmit = t2;
  let t3;
  if ($[2] !== content.emailPlaceholder || $[3] !== content.messagePlaceholder || $[4] !== content.namePlaceholder || $[5] !== content.subjectPlaceholder || $[6] !== content.submitLabel || $[7] !== content.successMessage || $[8] !== form || $[9] !== loading || $[10] !== submitted) {
    t3 = <div className="relative w-full rounded-3xl border border-border bg-card p-8 shadow-sm sm:p-10"><AnimatePresence mode="wait">{submitted ? <motion.div key="success" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} transition={{
          duration: 0.45
        }} className="flex flex-col items-center justify-center gap-5 py-10 text-center"><div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10"><CheckCircle2 className="h-8 w-8 text-primary" strokeWidth={1.5} /></div><div className="flex flex-col gap-2"><p className="text-xl font-semibold text-foreground">Message sent!</p><p className="max-w-xs text-sm leading-relaxed text-muted-foreground">{content.successMessage}</p></div><button onClick={() => {
            setSubmitted(false);
            setForm(INITIAL);
          }} className="mt-2 text-sm font-medium text-primary underline underline-offset-2 transition-colors hover:text-primary/80">Send another message</button></motion.div> : <motion.form key="form" initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} onSubmit={handleSubmit} className="flex flex-col gap-5"><div className="grid grid-cols-1 gap-5 sm:grid-cols-2"><Field label="Full name" required={true}><input required={true} type="text" name="name" value={form.name} onChange={handleChange} placeholder={content.namePlaceholder} className={inputCls} /></Field><Field label="Email address" required={true}><input required={true} type="email" name="email" value={form.email} onChange={handleChange} placeholder={content.emailPlaceholder} className={inputCls} /></Field></div><Field label="Subject" required={true}><input required={true} type="text" name="subject" value={form.subject} onChange={handleChange} placeholder={content.subjectPlaceholder} className={inputCls} /></Field><Field label="Message" required={true}><textarea required={true} name="message" value={form.message} onChange={handleChange} rows={5} placeholder={content.messagePlaceholder} className={cn(inputCls, "resize-none")} /></Field><button type="submit" disabled={loading} className={cn("inline-flex items-center justify-center gap-2 rounded-full bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all duration-300", "hover:scale-[1.02] hover:bg-primary/90 active:scale-[0.98]", "disabled:pointer-events-none disabled:opacity-60")}>{loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-primary-foreground/30 border-t-primary-foreground" /> : <Send className="h-4 w-4" strokeWidth={2} />}{content.submitLabel}</button></motion.form>}</AnimatePresence></div>;
    $[2] = content.emailPlaceholder;
    $[3] = content.messagePlaceholder;
    $[4] = content.namePlaceholder;
    $[5] = content.subjectPlaceholder;
    $[6] = content.submitLabel;
    $[7] = content.successMessage;
    $[8] = form;
    $[9] = loading;
    $[10] = submitted;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp(r) {
  return setTimeout(r, 1200);
}
const inputCls = "w-full rounded-xl border border-input bg-background px-4 py-3 text-sm text-foreground placeholder:text-muted-foreground outline-none transition-all duration-200 focus:border-primary/60 focus:ring-2 focus:ring-primary/15";
function Field(t0) {
  const $ = _c(8);
  const {
    label,
    required,
    children
  } = t0;
  let t1;
  if ($[0] !== required) {
    t1 = required && <span className="ml-1 text-primary">*</span>;
    $[0] = required;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== label || $[3] !== t1) {
    t2 = <label className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{label}{t1}</label>;
    $[2] = label;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== children || $[6] !== t2) {
    t3 = <div className="flex flex-col gap-1.5">{t2}{children}</div>;
    $[5] = children;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}